"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"

const FALLBACK_RELATED = [
  "wine-refrigerator-calculator",
  "dual-zone-wine-cooler-calculator",
  "energy-cost-calculator",
  "refrigerator-size-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Wine Cooler Energy Calculator",
  category: "Energy & Costs" as const,
  mediumDescription:
    "Estimate yearly electricity use and cost for a single-zone vs multi-zone wine cooler. Uses ENERGY STAR-style consumption rates per cubic foot and the US average residential electricity price.",
  faqs: [
    {
      question: "Why does adding zones use more energy?",
      answer:
        "Each independent zone has its own thermostat and either a second cold loop or an extra evaporator. That additional control hardware adds roughly 10% to baseline kWh per added zone in our model.",
    },
    {
      question: "What electricity rate does this calculator use?",
      answer:
        "We use $0.13/kWh, close to the US residential average reported by the EIA. If your utility rate is higher, multiply the annual cost by your local rate ÷ 0.13.",
    },
    {
      question: "Are wine coolers ENERGY STAR rated?",
      answer:
        "Yes. ENERGY STAR certifies wine coolers under the same program as refrigerators. Look for the label and an estimated annual kWh number on the energy guide sticker.",
    },
    {
      question: "Does usage intensity really change yearly kWh?",
      answer:
        "Yes — heavier door-opening, warmer rooms, and frequent restocking force the compressor to run more. We model light use at -10%, moderate at baseline, heavy at +15%.",
    },
    {
      question: "Is multi-zone worth the extra electricity?",
      answer:
        "If you actually serve from the unit and need correct temperatures for both reds and whites, yes — the cost difference is usually under $10/year and you avoid temperature shocking your wine.",
    },
  ],
  relatedTools: ["wine-refrigerator-calculator", "dual-zone-wine-cooler-calculator", "energy-cost-calculator"],
}

export function MultiZoneEnergyCalculatorPageClient() {
  const [zones, setZones] = useState("2")
  const [fridgeSize, setFridgeSize] = useState("25")
  const [usage, setUsage] = useState("moderate")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const numZones = parseInt(zones)
    const size = parseFloat(fridgeSize)
    if (!numZones || !size || numZones < 1 || size <= 0) return null

    let singleZoneKwh = size * 20
    if (usage === "light") singleZoneKwh *= 0.9
    else if (usage === "heavy") singleZoneKwh *= 1.15

    let multiZoneKwh = singleZoneKwh * (1 + (numZones - 1) * 0.1)
    multiZoneKwh *= 0.95

    const electricityRate = 0.13
    const singleZoneCost = singleZoneKwh * electricityRate
    const multiZoneCost = multiZoneKwh * electricityRate
    const difference = multiZoneCost - singleZoneCost

    let worthIt: string
    if (difference < 10) worthIt = "Minimal cost increase — worth it for flexibility"
    else if (difference < 20) worthIt = "Reasonable cost — good for specific storage needs"
    else if (difference < 30) worthIt = "Moderate increase — justify with usage patterns"
    else worthIt = "Significant increase — make sure you need multiple zones"

    return {
      singleZoneKwh: Math.round(singleZoneKwh),
      multiZoneKwh: Math.round(multiZoneKwh),
      singleZoneCost: parseFloat(singleZoneCost.toFixed(2)),
      multiZoneCost: parseFloat(multiZoneCost.toFixed(2)),
      difference: parseFloat(difference.toFixed(2)),
      worthIt,
    }
  }, [zones, fridgeSize, usage])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="What does an extra zone"
      heroTitleItalic="actually"
      heroTitlePost="cost per year?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~15 sec", "EIA residential rate · 365-day model"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "$0.13", label: "Per kWh", note: "US average" },
            { value: "20", label: "kWh / cu ft", note: "Baseline" },
            { value: "+10%", label: "Per added zone", note: "Modeled" },
            { value: "365", label: "Days / year", note: "Annual cost" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "wine-cooler-energy-calculator")}
      cta={{
        title: "Right zone count. Right cubic feet. Lower bill.",
        body: "Browse single- and dual-zone wine coolers from compact 6-bottle units to 150-bottle built-ins.",
        buttonLabel: "Browse wine coolers",
        buttonHref: "/collections/wine-fridges",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Zones, size,
                <br />
                use intensity.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                We compare a single-zone baseline against your configured zones at $0.13/kWh.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Number of zones
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={zones}
                      onChange={(e) => setZones(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">zones</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Cooler size
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={fridgeSize}
                      onChange={(e) => setFridgeSize(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">cu ft</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Usage pattern
                  </label>
                  <select
                    value={usage}
                    onChange={(e) => setUsage(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="light">Light — rarely opened</option>
                    <option value="moderate">Moderate — typical home use</option>
                    <option value="heavy">Heavy — daily entertaining</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Heavy use adds roughly 15% to baseline kWh.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }}
                />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Multi-zone yearly cost
                  </div>
                  <div
                    className="font-black"
                    style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}
                  >
                    {`$${result?.multiZoneCost ?? 0}`}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">/yr</span>
                  </div>
                  <div className="mt-4 text-[15px] text-white/85 leading-[1.5]">
                    {result?.worthIt ?? "Enter zone count and size."}
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Single zone</div>
                      <div className="text-[16px] sm:text-[20px] font-black tracking-tight">{`$${result?.singleZoneCost ?? "—"}`}</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Difference</div>
                      <div className="text-[16px] sm:text-[20px] font-black tracking-tight">{`$${result?.difference ?? "—"}`}</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">kWh / yr</div>
                      <div className="text-[16px] sm:text-[20px] font-black tracking-tight">{result?.multiZoneKwh ?? "—"}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Shop low-kWh wine coolers"
          title="ENERGY STAR-style wine coolers"
          description="Compressor and thermoelectric units with documented annual kWh — sized 12 to 150 bottles."
          collectionType="wine-cooler"
          fallbackCollectionType="counter-depth"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                EIA average. <em className="italic text-[#ff9951]">Real</em> compressor math.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Baseline 20 kWh per cubic foot per year matches typical wine-cooler energy guide labels. Cost uses the EIA US residential average ($0.13/kWh).
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Single-zone baseline",
                  d: "Cubic feet × 20 kWh/yr, then ±10–15% based on usage intensity (light / moderate / heavy).",
                },
                {
                  n: "02",
                  t: "Multi-zone adjustment",
                  d: "Each zone above one adds 10% to baseline kWh, then a 5% reduction for smart per-zone setpoints.",
                },
                {
                  n: "03",
                  t: "Annual cost",
                  d: "Yearly kWh × $0.13 = annual operating cost. Difference column shows the multi-zone premium.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Counter-depth fridges with multi-zone settings"
          description="Built-in 24-inch units that double as beverage and wine cold storage with zoned compartments."
          collectionType="counter-depth"
          fallbackCollectionType="counter-depth"
        />
      </section>
    </ToolPageShell>
  )
}
